import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import {
    RemoteDepositCaptureService,
    RemoteDepositImage,
} from '@treasury/domain/remote-deposit-capture';
import { LabeledList, TmBaseComponent } from '@treasury/presentation';
import { clockIcon, moneyCheckIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-confirm-dialog';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { tmIf } from '@treasury/utils/lit-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
    AddCheckPayload,
    DeleteDepositPayload,
    RemoteDepositEvent,
    RemoveCheckPayload,
    SubmitDepositPayload,
} from '../containers/remote-deposit.types';
import { DepositVm, ItemImageVm, RemoteDepositItemVm } from '../view-models';

export const tagName = 'deposit-review-step';
/**
 * @fires DepositReviewStep#submit-deposit - Event fired when the user clicks the submit button
 * @fires DepositReviewStep#delete-deposit - Event fired when the user clicks the delete button
 * @fires DepositReviewStep#add-check - Event fired when the user clicks the add check button
 * @fires DepositReviewStep#remove-check - Event fired when the user clicks the remove check button
 */

@customElement(tagName)
export class DepositReviewStep extends TmBaseComponent {
    @property({ attribute: false })
    public deposit!: DepositVm;

    @property({ attribute: false })
    public checkDetailOpen = false;

    protected verifiedPropNames: readonly (keyof this)[] = ['deposit'];

    @InjectProperty()
    declare private readonly remoteDepositCaptureService: RemoteDepositCaptureService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private selectedCheck?: RemoteDepositItemVm;

    @state()
    private selectedCheckImages?: ItemImageVm;

    protected render() {
        const addCheckLabel = this.deposit.checks.length > 0 ? 'Add another check' : 'Add a check';
        return html`<tm-section class="pt-6 px-6">
                <div class="flex justify-between pb-2">
                    <div class="left">
                        <div class="text-xl">Deposit Total</div>
                        <div class="text-3xl font-light">${this.deposit.formattedTotalAmount}</div>
                    </div>
                    <div class="text-sm">
                        <div class="flex items-center">
                            <tm-icon
                                .icon=${this.deposit.statusIcon}
                                .color=${this.deposit.statusColor}
                            ></tm-icon>
                            ${this.deposit.statusDescription}
                        </div>
                    </div>
                </div>
                <tm-labeled-list
                    class="pb-4 border-b border-dashed"
                    .list=${new LabeledList(
                        {
                            depositDate: this.deposit.formattedDepositDate,
                            location: this.deposit.locationName,
                        },
                        ['location', 'depositDate']
                    )}
                ></tm-labeled-list>
                <tm-button class="w-full" importance="secondary" @click=${this.addCheck}>
                    ${addCheckLabel}
                </tm-button>
                <div class="py-4">
                    <h4 class="font-medium pb-2">
                        Checks <tm-badge>${this.deposit.totalCount}</tm-badge>
                    </h4>
                    ${this.renderChecks()}
                </div>
            </tm-section>
            ${this.renderFooter()}

            <tm-bottom-sheet
                .open=${this.checkDetailOpen}
                @close=${() => {
                    this.selectedCheck = undefined;
                    this.checkDetailOpen = false;
                }}
            >
                <span slot="header-center">Entered Check</span>
                <tm-section class="pt-6 px-6"> ${this.renderCheckDetail()} </tm-section>
                <tm-footer
                    .buttonConfig=${[
                        {
                            error: true,
                            text: 'Remove check from deposit',
                            onClick: () => {
                                this.removeSelectedCheck();
                            },
                        },
                    ]}
                ></tm-footer>
            </tm-bottom-sheet>`;
    }

    private submitDeposit() {
        this.dispatchEvent(new CustomEvent<SubmitDepositPayload>(RemoteDepositEvent.SubmitDeposit));
    }

    private deleteDeposit() {
        this.dispatchEvent(new CustomEvent<DeleteDepositPayload>(RemoteDepositEvent.DeleteDeposit));
    }

    private addCheck() {
        this.dispatchEvent(new CustomEvent<AddCheckPayload>(RemoteDepositEvent.AddCheck));
    }

    private removeSelectedCheck() {
        this.dispatchEvent(
            new CustomEvent<RemoveCheckPayload>(RemoteDepositEvent.RemoveCheck, {
                detail: this.selectedCheck,
            })
        );
    }

    private async openCheckDetail() {
        this.selectedCheckImages = new ItemImageVm({});
        this.selectedCheckImages.frontImage = this.selectedCheck?.frontImage ?? '';
        this.selectedCheckImages.rearImage = this.selectedCheck?.rearImage ?? '';

        if (!this.selectedCheck?.frontImage || !this.selectedCheck?.rearImage) {
            const result = await this.remoteDepositCaptureService.getItemImages(
                this.selectedCheck?.itemReference as string
            );
            this.selectedCheckImages = new ItemImageVm(result as RemoteDepositImage);
        }

        this.checkDetailOpen = true;
    }

    private async showConfirmDialog() {
        if (this.dialogService.isDialogOpen) {
            return;
        }

        const { reason } = await this.dialogService.open(
            'Are you sure you want to delete this deposit? All of the scanned items will be lost.',
            'Delete Deposit',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Yes, delete',
                    },
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'No, go back',
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );

        if (reason === DynamicComponentExitReason.Confirm) {
            this.deleteDeposit();
        }
    }

    private renderChecks() {
        return this.deposit.checks?.map(
            check =>
                html` <button
                    class="flex justify-between w-full py-2 text-sm"
                    @click=${() => {
                        this.selectedCheck = check;
                        this.openCheckDetail();
                    }}
                >
                    <div class="flex items-center text-left mr-3">
                        <div class="mr-1">
                            <tm-icon .icon=${clockIcon} .color=${'orange'}></tm-icon>
                        </div>
                        <div>
                            <div class="text-medium"></div>
                            <div class="text-xs" style="color: 'orange'">Pending</div>
                        </div>
                        <div ${tmIf(check.customerName)} class="text-xs ml-1">
                            (${check.customerName})
                        </div>
                    </div>
                    <div class="flex justify-end text-right items-center">
                        <tm-icon .icon=${moneyCheckIcon} class="mr-2"></tm-icon>
                        <span class="font-medium">
                            ${check.amount.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                        </span>
                    </div>
                </button>`
        );
    }

    private renderCheckDetail() {
        if (!this.selectedCheck) {
            return nothing;
        }
        return html` <div class="text-3xl font-light pb-2">
                ${this.selectedCheck?.formattedAmount}
            </div>
            <tm-labeled-list
                class="mb-4 pb-4 border-b border-dashed"
                .list=${this.selectedCheck?.getLabeledList(this.deposit.locationName)}
            ></tm-labeled-list>
            <h4 class="pb-2 font-medium">Check Images</h4>
            <div class="my-2">
                <img
                    src="${ifDefined(this.selectedCheckImages?.frontImage)}"
                    alt="Front of check"
                />
            </div>
            <div class="my-2">
                <img src="${ifDefined(this.selectedCheckImages?.rearImage)}" alt="Back of check" />
            </div>`;
    }

    private renderFooter() {
        return html` <tm-footer
            direction="row"
            .buttonConfig=${[
                {
                    text: `Delete Deposit`,
                    error: true,
                    onClick: () => this.showConfirmDialog(),
                    disabled: !this.deposit.canDeleteDeposit,
                },
                {
                    text: `Deposit(${this.deposit.totalCount})`,
                    onClick: () => this.submitDeposit(),
                    disabled: !this.deposit.canSubmitDeposit,
                },
            ]}
        >
        </tm-footer>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DepositReviewStep;
    }
}
